import React, { forwardRef } from "react";
import { useObjectRef } from "react-aria";

import { JSONCharBlock, FeaturesValuePropBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ContentLayout } from "./utils";

import styles from "./FeatureValueProp.module.scss";

interface Props {
    value: Value;
    index: number;
    hotspot: JSONCharBlock[number];
    imageBounds: DOMRect | null;
    containerRect: DOMRect | null;
    hideBody: boolean;
    colliding: boolean;
    translateY: number;
    contentLayout: ContentLayout;
}

interface CustomCss extends React.CSSProperties {
    "--top"?: string;
    "--left"?: string;
    "--hotspot-right-offset"?: string;
    "--hotspot-left-offset"?: string;
    "--width"?: string;
    "--translate-y"?: string;
    "--vertical-pointer-line-height"?: string;
    "--vertical-pointer-line-height-offset"?: string;
}

export const FeatureValueProp = forwardRef<HTMLDivElement, Props>(
    (props, ref) => {
        const {
            value,
            index,
            hotspot,
            imageBounds,
            containerRect,
            hideBody,
            colliding,
            translateY,
            contentLayout,
        } = props;
        ref = useObjectRef(ref);

        // hotspot is set by hotspots[index] which could be undefined, but
        // we haven't configured TS to be mad at that
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!hotspot || !imageBounds || !containerRect) return null;

        // Assumption: image is always as tall as the entire block
        const top = `${imageBounds.top + hotspot.y * imageBounds.height}px`;

        const hotspotLeftOffset = hotspot.x * imageBounds.width;
        const hotspotRightOffset = imageBounds.width - hotspotLeftOffset;

        const verticalLineHeightOffset = translateY > 0 ? -translateY : 0;

        // Assumption: outside boundary of value prop is going to touch either the
        // left or right side of the container
        const valuePropWidth =
            value.alignment == "alignLeft"
                ? imageBounds.left + hotspotLeftOffset
                : containerRect.width - (imageBounds.left + hotspotLeftOffset);

        return (
            <div
                ref={ref}
                className={concatClassNames([
                    styles[value.alignment ?? "alignLeft"],
                    contentLayout === "overLine" ? styles.overLine : undefined,
                    colliding ? styles.colliding : undefined,
                ])}
                style={
                    {
                        "--top": top,
                        "--width": `${valuePropWidth}px`,
                        "--hotspot-left-offset": `${hotspotLeftOffset}px`,
                        "--hotspot-right-offset": `${hotspotRightOffset}px`,
                        "--translate-y": `${translateY}px`,
                        "--vertical-pointer-line-height": `${Math.abs(translateY)}px`,
                        "--vertical-pointer-line-height-offset": `${verticalLineHeightOffset}px`,
                    } as CustomCss
                }
            >
                <div className={styles.inner}>
                    <div
                        className={
                            value.icon
                                ? styles.contentsWithIcon
                                : styles.contents
                        }
                    >
                        <h3 className={styles.valuePropTitle}>
                            <span className={styles.titleNumber}>
                                {index + 1}.{" "}
                            </span>
                            <span className={styles.titleSpan}>
                                {value.title}
                            </span>
                            {value.icon && (
                                <ImageChooserBlock value={value.icon} />
                            )}
                        </h3>
                        <p
                            className={
                                hideBody ? styles.hiddenBody : styles.body
                            }
                        >
                            {value.body}
                        </p>
                    </div>
                </div>
            </div>
        );
    },
);
